import {type ReactNode, useState} from 'react'
import {IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon, XIcon} from '@primer/octicons-react'
import styles from './ActionF.module.css'

export type ActionSeverity = 'high' | 'med' | 'low'

export const ActionF = ({
  header,
  collapsedContent,
  expandedContent,
  onDismiss,
  isOpen: controlledIsOpen,
  onToggle,
  severity = 'med',
}: {
  header: ReactNode
  collapsedContent?: ReactNode
  expandedContent?: ReactNode
  onDismiss?: () => void
  isOpen?: boolean
  onToggle?: (open: boolean) => void
  severity?: ActionSeverity
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const isOpen = controlledIsOpen ?? internalIsOpen
  const toggle = () => {
    const next = !isOpen
    setInternalIsOpen(next)
    onToggle?.(next)
  }

  const body = isOpen ? expandedContent : collapsedContent

  return (
    <li className={styles.container}>
      <span className="sr-only">{severity} priority</span>
      <div className={styles.header}>
        {severity === 'high' && <span className={styles.highPriorityStripe} aria-hidden="true" />}
        <div className="d-flex flex-shrink-0 pl-2">
          {/* Chevron is the sole accessible disclosure toggle */}
          <IconButton
            onClick={toggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
            size="small"
            variant="invisible"
          />
        </div>
        {/* Header content — interactive elements like author links are accessible here */}
        <div className={styles.toggleArea}>{header}</div>
        {onDismiss && (
          <IconButton
            aria-label="Dismiss"
            icon={XIcon}
            variant="invisible"
            size="small"
            onClick={onDismiss}
            className="mr-2 flex-shrink-0"
          />
        )}
      </div>

      {body !== undefined && body !== null && body !== false ? <div>{body}</div> : null}
    </li>
  )
}
